// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LeaveFeedback from "@conklin/App/Components/Product/LeaveFeedback";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    // XNG-Change
    printLink?: LinkPresentationProps;
    copyLink?: LinkPresentationProps;
    feedbackLink?: LinkPresentationProps;
    separator?: InjectableCss;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            margin-top: 30px;
            text-align: center;
        `,
    },
};

const styles = shareLinkStyles;

const ProductDetailsShareLink: React.FC<Props> = ({ product }) => {
    const toasterContext = React.useContext(ToasterContext);

    if (!product) {
        return null;
    }

    const copyLink = () => {
        const pageUrl = window.location.href;
        navigator.clipboard
            .writeText(pageUrl)
            .then(() => {
                toasterContext.addToast({
                    body: translate("Link copied to clipboard!"),
                    messageType: "success",
                });
            })
            .catch(err => {
                toasterContext.addToast({
                    body: translate("Failed to copy the link!"),
                    messageType: "danger",
                });
            });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductShareLink text={translate("Email")} extendedStyles={styles.link} />
            {/* XNG-Changes: add other links */}
            <Link aria-label={translate("Print")} {...styles.printLink} onClick={openPrintDialog}>
                {translate("Print")}
            </Link>
            <Link aria-label={translate("Copy Link")} {...styles.copyLink} onClick={copyLink}>
                {translate("Copy Link")}
            </Link>
            <LeaveFeedback itemCode={product.productNumber} sku={product.detail?.sku} />
            {/* XNG-Changes: add separator for mobile */}
            <StyledWrapper {...styles.separator} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsShareLink)),
    definition: {
        displayName: "Share Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
